import { ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Tabs, Text, cn, type TabsItem } from '@cloudflare/kumo'
import { usePageHeaderSlot } from './page-header-context'

export const KUMO_PAGE_HEADER_VARIANTS = {
  spacing: {
    compact: {
      classes: 'gap-1',
      description: 'Compact spacing between header elements',
    },
    base: {
      classes: 'gap-2',
      description: 'Default spacing between header elements',
    },
    relaxed: {
      classes: 'gap-4',
      description: 'Relaxed spacing for more prominent headers',
    },
  },
} as const

export const KUMO_PAGE_HEADER_DEFAULT_VARIANTS = {
  spacing: 'base',
} as const

export type KumoPageHeaderSpacing = keyof typeof KUMO_PAGE_HEADER_VARIANTS.spacing

export interface KumoPageHeaderVariantsProps {
  spacing?: KumoPageHeaderSpacing
}

export function pageHeaderVariants({
  spacing = KUMO_PAGE_HEADER_DEFAULT_VARIANTS.spacing,
}: KumoPageHeaderVariantsProps = {}) {
  return cn('flex flex-col', KUMO_PAGE_HEADER_VARIANTS.spacing[spacing].classes)
}

export interface PageHeaderProps extends KumoPageHeaderVariantsProps {
  breadcrumbs: ReactNode
  title?: string
  titleIcon?: ReactNode
  description?: string
  tabs?: TabsItem[]
  defaultTab?: string
  onValueChange?: (value: string) => void
  className?: string
  children?: React.ReactNode
}

export function PageHeader({
  breadcrumbs,
  title,
  titleIcon,
  description,
  tabs,
  defaultTab,
  onValueChange,
  spacing = 'base',
  className,
  children,
}: PageHeaderProps) {
  const { pinned, setPinned, mainNode, barLeftNode, barPinnedNode } = usePageHeaderSlot()
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!mainNode || !sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setPinned(!entry.isIntersecting)
      },
      { root: mainNode, threshold: 0 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [mainNode, setPinned])

  return (
    <div className={cn(pageHeaderVariants({ spacing }), className)} style={{ marginBottom: 24 }}>
      <div ref={sentinelRef} aria-hidden style={{ height: 0 }} />

      {(title || description) && (
        <div className="flex flex-col gap-2 py-3 px-3">
          {title && (
            <div className="flex items-center gap-2">
              {titleIcon}
              <Text variant="heading1" as="h1" DANGEROUS_className="text-kumo-default">
                {title}
              </Text>
            </div>
          )}
          {description && (
            <Text as="p" variant="secondary">
              {description}
            </Text>
          )}
        </div>
      )}

      {tabs && (
        <div className="flex w-full items-center justify-between border-b border-kumo-line pt-1 pb-3 px-3">
          <Tabs
            tabs={tabs}
            selectedValue={defaultTab}
            onValueChange={(nextValue) => {
              const stringValue = String(nextValue)
              onValueChange?.(stringValue)
            }}
          />

          {!pinned && children && <div className="flex items-center gap-2">{children}</div>}
        </div>
      )}

      {!tabs && !pinned && children && (
        <div className="flex items-center justify-end px-3 pb-3">
          <div className="flex items-center gap-2">{children}</div>
        </div>
      )}

      {barLeftNode && createPortal(breadcrumbs, barLeftNode)}

      {pinned &&
        barPinnedNode &&
        createPortal(
          <>
            {titleIcon}
            <Text as="span" variant="heading1" DANGEROUS_className="text-kumo-default truncate">
              {title}
            </Text>
            {children && <div className="ml-auto flex items-center gap-2 shrink-0">{children}</div>}
          </>,
          barPinnedNode,
        )}
    </div>
  )
}
